import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1f1f1f', // Brand dark
      light: '#737373',
      dark: '#1f1f1f',
      contrastText: '#fff',
    },
    secondary: {
      main: '#22c55e', // Soft green accent
      light: '#4ade80',
      dark: '#16a34a',
      contrastText: '#fff',
    },
    warning: {
      main: '#fbbf24',
      light: '#fde68a',
      dark: '#b45309',
      contrastText: '#fff',
    },
    info: {
      main: '#737373',
      light: '#f5f7fa',
      dark: '#334155',
      contrastText: '#fff',
    },
    success: {
      main: '#22c55e',
      light: '#bbf7d0',
      dark: '#15803d',
      contrastText: '#fff',
    },
    error: {
      main: '#f44336',
      light: '#ef9a9a',
      dark: '#c62828',
      contrastText: '#fff',
    },
    divider: '#e0e0e0',
    background: {
      default: '#f5f7fa', // Clean light gray
      paper: '#fff',
    },
    text: {
      primary: '#1f1f1f',
      secondary: '#737373',
    },
    // Custom color palette extensions
    brand: {
      orange: '#f97316',
      orangeLight: '#fbbf24',
      orangeDark: '#ea580c',
      blue: '#3b82f6',
      blueLight: '#60a5fa',
      blueDark: '#2563eb',
      cyan: '#00d4ff',
      cyanLight: '#0ea5e9',
      purple: '#8b5cf6',
      purpleLight: '#a78bfa',
      purpleDark: '#7c3aed',
      green: '#22c55e',
      greenLight: '#34d399',
      greenDark: '#16a34a',
      red: '#ef4444',
      redLight: '#f87171',
      redDark: '#dc2626',
      gray: {
        50: '#f8fafc',
        100: '#f1f5f9',
        200: '#e2e8f0',
        300: '#cbd5e1',
        400: '#94a3b8',
        500: '#737373',
        600: '#475569',
        700: '#334155',
        800: '#1e293b',
        900: '#0f172a',
      }
    },
    glass: {
      background: 'rgba(255, 255, 255, 0.6)',
      backgroundDark: 'rgba(17, 24, 39, 0.9)',
      backgroundMedium: 'rgba(31, 41, 55, 0.5)',
      border: 'rgba(203, 213, 225, 0.5)',
      borderDark: 'rgba(55, 65, 81, 0.5)',
    },
    gradients: {
      primary: 'linear-gradient(135deg, #3b82f6 0%, #00d4ff 50%, #8b5cf6 100%)',
      orange: 'linear-gradient(135deg, #f97316 0%, #fbbf24 100%)',
      blue: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 50%, #3b82f6 100%)',
      purple: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)',
      text: 'linear-gradient(135deg, #f97316 0%, #00d4ff 50%, #8b5cf6 100%)',
    }
  },
  typography: {
    fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 800,
      fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 700,
      fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
    },
    h3: {
        fontSize: '1.75rem',
        fontWeight: 700,
      fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
    },
    h4: {
        fontSize: '1.5rem',
        fontWeight: 600,
      fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
    },
    h5: {
        fontSize: '1.25rem',
        fontWeight: 600,
      fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
    },
    h6: {
        fontSize: '1.1rem',
        fontWeight: 600,
      fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
    },
  },
  // Custom spacing scale
  spacing: (factor) => `${0.25 * factor}rem`,
  // Custom breakpoints
  breakpoints: {
    values: {
      xs: 0,
      sm: 640,
      md: 768,
      lg: 1024,
      xl: 1280,
    },
  },
  // Custom shape overrides
  shape: {
    borderRadius: 8,
    borderRadiusLg: 12,
    borderRadiusXl: 16,
  },
  // Custom effects and filters
  custom: {
    effects: {
      blur: {
        sm: '8px',
        md: '12px',
        lg: '16px',
      },
      shadow: {
        sm: '0 4px 16px rgba(30,41,59,0.07)',
        md: '0 8px 32px rgba(0, 0, 0, 0.1)',
        lg: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        glass: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      },
      backdrop: {
        sm: 'blur(8px)',
        md: 'blur(12px)',
        lg: 'blur(16px)',
      },
    },
    animation: {
      duration: {
        fast: '0.2s',
        normal: '0.3s',
        slow: '0.5s',
      },
      timing: 'ease',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-10px) rotate(1deg); }
          66% { transform: translateY(5px) rotate(-1deg); }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        
        @keyframes orbit-outer {
          from { 
            transform: translate(-50%, -50%) rotate(0deg) translateX(160px) rotate(0deg); 
          }
          to { 
            transform: translate(-50%, -50%) rotate(360deg) translateX(160px) rotate(-360deg); 
          }
        }
        
        @keyframes orbit-inner {
          from { 
            transform: translate(-50%, -50%) rotate(0deg) translateX(96px) rotate(0deg); 
          }
          to { 
            transform: translate(-50%, -50%) rotate(360deg) translateX(96px) rotate(-360deg); 
          }
        }
        
        @keyframes hub-pulse {
          0%, 100% { 
            box-shadow: 0 0 20px rgba(59, 130, 246, 0.4), 0 0 40px rgba(168, 85, 247, 0.2);
          }
          50% { 
            box-shadow: 0 0 30px rgba(59, 130, 246, 0.6), 0 0 60px rgba(168, 85, 247, 0.4);
          }
        }
        
        @media (max-width: 768px) {
          @keyframes orbit-outer {
            from { 
              transform: translate(-50%, -50%) rotate(0deg) translateX(120px) rotate(0deg); 
            }
            to { 
              transform: translate(-50%, -50%) rotate(360deg) translateX(120px) rotate(-360deg); 
            }
          }
          
          @keyframes orbit-inner {
            from { 
              transform: translate(-50%, -50%) rotate(0deg) translateX(72px) rotate(0deg); 
            }
            to { 
              transform: translate(-50%, -50%) rotate(360deg) translateX(72px) rotate(-360deg); 
            }
          }
        }
        
        @media (max-width: 480px) {
          @keyframes orbit-outer {
            from { 
              transform: translate(-50%, -50%) rotate(0deg) translateX(100px) rotate(0deg); 
            }
            to { 
              transform: translate(-50%, -50%) rotate(360deg) translateX(100px) rotate(-360deg); 
            }
          }
          
          @keyframes orbit-inner {
            from { 
              transform: translate(-50%, -50%) rotate(0deg) translateX(60px) rotate(0deg); 
            }
            to { 
              transform: translate(-50%, -50%) rotate(360deg) translateX(60px) rotate(-360deg); 
            }
          }
        }
        /* Focus visible outline for accessibility */
        :focus-visible {
          outline: 3px solid #3b82f6;
          outline-offset: 2px;
        }
        
        /* Mobile viewport height fixes */
        html, body {
          height: 100%;
          overflow-x: hidden;
        }
        
        #root {
          min-height: 100vh;
          min-height: 100dvh; /* Dynamic viewport height for mobile */
        }
        
        /* Prevent horizontal scroll on mobile */
        body {
          overflow-x: hidden;
          position: relative;
        }
        
        /* Mobile-specific height adjustments */
        @media (max-width: 768px) {
          /* Use dynamic viewport height for mobile */
          .mobile-height-fix {
            min-height: 100dvh;
            min-height: 100vh; /* Fallback for older browsers */
          }
          
          /* Prevent content overflow on mobile */
          .mobile-container {
            max-width: 100vw;
            overflow-x: hidden;
          }
        }
        
        /* Handle mobile browser UI changes */
        @supports (height: 100dvh) {
          .mobile-height-fix {
            min-height: 100dvh;
          }
        }
      `,
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 600,
          '&:focus-visible': {
            boxShadow: '0 0 0 3px rgba(59,130,246,0.45)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          boxShadow: '0 4px 16px rgba(30,41,59,0.07)',
        },
      },
    },
    MuiPaper: {
        styleOverrides: {
          root: {
          borderRadius: 20,
          },
        },
    },
    MuiTableHead: {
        styleOverrides: {
            root: {
          backgroundColor: '#f5f7fa',
            }
        }
    }
  },
});

export default theme; 